/**
 * The `?` sheet. It renders the SHORTCUTS registry directly — there is no second
 * list of keys anywhere in the product, so this can never go stale.
 *
 * It reads the whole entry, not just `group`. `Shortcut.requires` has carried
 * "Context the action needs; the sheet greys out what does not apply" since the
 * registry was written and nothing read it, so on `/tasks` all seven ON A RECORD
 * rows printed at `--text-secondary` with no `aria-disabled` — the same
 * typography as the seven list keys that work — while every one of them was
 * inert. `lib/shortcutContext` is the other half: surfaces declare what they
 * are, this decides what applies.
 */

import { Link } from 'react-router-dom';

import { Dialog, Kbd } from '@/components/ui';
import { cn } from '@/lib/cn';
import { ROUTES } from '@/lib/constants';
import {
  SHORTCUTS,
  SHORTCUT_GROUP_ORDER,
  useHotkeyScope,
  type Shortcut,
  type ShortcutGroup,
} from '@/lib/keyboard';
import { useShortcutContexts, type ShortcutContexts } from '@/lib/shortcutContext';

/* ────────────────────────────────────────────────────────────────────────────
   Imperative open — used by the user menu and the command palette
   ──────────────────────────────────────────────────────────────────────────── */

export const SHORTCUT_SHEET_EVENT = 'af:shortcuts';

export function openShortcutSheet(): void {
  window.dispatchEvent(new CustomEvent(SHORTCUT_SHEET_EVENT));
}

/* ────────────────────────────────────────────────────────────────────────────
   Key chips
   ──────────────────────────────────────────────────────────────────────────── */

export interface ShortcutChips {
  keys: string[];
  variant: 'chord' | 'then';
}

/**
 * `'mod+k'` → one chord of two chips; `'g i'` → two chips joined by "then".
 * `Kbd` resolves `mod` to ⌘ or Ctrl for the current platform.
 */
export function shortcutChips(spec: string): ShortcutChips {
  const chords = spec.trim().split(/\s+/).filter(Boolean);
  if (chords.length > 1) return { keys: chords, variant: 'then' };
  return { keys: (chords[0] ?? spec).split('+').filter(Boolean), variant: 'chord' };
}

/** Convenience for anything that just wants to render a spec. */
export function SpecKbd({
  spec,
  size = 'md',
  muted = false,
}: {
  spec: string;
  size?: 'sm' | 'md';
  /** The key is printed for reference but does nothing where the reader is. */
  muted?: boolean;
}) {
  const { keys, variant } = shortcutChips(spec);
  return (
    <Kbd
      keys={keys}
      variant={variant}
      size={size}
      className={muted ? '[&_kbd]:border-line-subtle [&_kbd]:text-faint' : undefined}
    />
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Sheet
   ──────────────────────────────────────────────────────────────────────────── */

const GROUP_TITLE: Record<ShortcutGroup, string> = {
  General: 'General',
  Navigation: 'Go to',
  List: 'In a list',
  Record: 'On a record',
};

const GROUP_HINT: Record<ShortcutGroup, string> = {
  General: 'Available everywhere.',
  Navigation: 'Press G, then the letter.',
  List: 'While a table or board has focus.',
  // Not "or selected". Selecting a row on a list does not put these keys in
  // play anywhere in the product, and the sentence was the only thing that
  // claimed it did.
  Record: 'While a record is open.',
};

/** What the group says on a screen that offers none of it. */
const GROUP_HINT_OFF: Record<ShortcutGroup, string> = {
  General: 'Available everywhere.',
  Navigation: 'Press G, then the letter.',
  List: 'Nothing on this screen is a list, so these do nothing here.',
  Record: 'Open a record first — these do nothing on this screen.',
};

/**
 * A row the reader can actually press, or a row printed for reference.
 *
 * `--text-faint` plus `aria-disabled` is the treatment `keyboard.ts` promised in
 * its own comment on `requires` and never got: seven dead keys used to print at
 * `--text-secondary`, typographically identical to the seven that worked.
 */
function Row({ shortcut, available }: { shortcut: Shortcut; available: boolean }) {
  return (
    <li
      aria-disabled={available ? undefined : 'true'}
      className="flex h-7 items-center justify-between gap-4"
    >
      <span
        className={cn('min-w-0 truncate text-base', available ? 'text-secondary' : 'text-faint')}
      >
        {shortcut.label}
      </span>
      <SpecKbd spec={shortcut.spec} muted={!available} />
    </li>
  );
}

function applies(shortcut: Shortcut, contexts: ShortcutContexts): boolean {
  return shortcut.requires === undefined || contexts[shortcut.requires];
}

function Group({ group, contexts }: { group: ShortcutGroup; contexts: ShortcutContexts }) {
  const rows = SHORTCUTS.filter((shortcut) => shortcut.group === group);
  if (rows.length === 0) return null;
  const anyApplies = rows.some((shortcut) => applies(shortcut, contexts));

  return (
    <section className="break-inside-avoid">
      <header className="border-b border-line-subtle pb-1.5">
        <h3 className="eyebrow">{GROUP_TITLE[group]}</h3>
      </header>
      <p className="mt-1.5 text-xs leading-snug text-faint">
        {anyApplies ? GROUP_HINT[group] : GROUP_HINT_OFF[group]}
      </p>
      <ul className="mt-1 flex flex-col">
        {rows.map((shortcut) => (
          <Row key={shortcut.id} shortcut={shortcut} available={applies(shortcut, contexts)} />
        ))}
      </ul>
    </section>
  );
}

export interface ShortcutSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShortcutSheet({ open, onOpenChange }: ShortcutSheetProps) {
  // While the sheet is up it owns the keyboard, so `g i` behind it stays quiet.
  useHotkeyScope('shortcut-sheet', open);
  // The sheet is an overlay, not a navigation: the surface underneath stays
  // mounted, so its claim is still live and the greying describes where the
  // reader actually is.
  const contexts = useShortcutContexts();

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Keyboard shortcuts"
      description="AlphaForm is built to be driven from the keyboard. Press ? at any time to bring this back."
      footer={
        <>
          <span className="mr-auto flex items-center gap-2 text-xs text-muted">
            Close
            <Kbd keys={['escape']} size="sm" />
          </span>
          {/* The reader who opened this hoping it was "help" gets pointed at
              the thing that actually is. A key list answers "how do I do this
              faster", never "what is this". */}
          <Link
            to={ROUTES.gettingStarted}
            onClick={() => onOpenChange(false)}
            className={cn(
              'text-xs text-secondary underline underline-offset-2',
              'hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 outline-[color:var(--focus)]',
            )}
          >
            New here? Start with Getting started
          </Link>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-x-8 gap-y-6 mobile:grid-cols-1">
        {SHORTCUT_GROUP_ORDER.map((group) => (
          <Group key={group} group={group} contexts={contexts} />
        ))}
      </div>
    </Dialog>
  );
}
